import type { CopilotAgentUsageMeterBody } from '@customerio/hydra-ui-api';
import { getApiClientErrorStatus, hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';

export type AgentUsageMeter = CopilotAgentUsageMeterBody;

/** Key prefix shared by every meter read; account params are appended per query. */
const AGENT_USAGE_METER_QUERY_KEY = 'getAgentUsageMeter';

/**
 * Marks every mounted meter read stale and refetches the active ones. Call
 * when usage may have just moved (an agent exchange finished) so the chat
 * pill and in-thread card update mid-session instead of waiting for a remount.
 *
 * The query client is imported lazily: a static import would put the app-wide
 * client (and its auth/error-handler graph) into every module that touches
 * this feature's barrel, breaking tests that partially mock those services.
 */
export async function invalidateAgentUsageMeter(): Promise<void> {
  const { queryClient } = await import('@/services/query-client');
  await queryClient.invalidateQueries({ queryKey: [AGENT_USAGE_METER_QUERY_KEY] });
}

/** Whether the error is the endpoint's "meter not enabled for this account" 404. */
function isMeterDisabled(error: unknown): boolean {
  return getApiClientErrorStatus(error) === 404;
}

/**
 * Loads the account's agent usage meter — the single read behind every meter
 * surface. The endpoint 404s when the account's meter feature flag is off;
 * that is the "surfaces stay hidden" signal, not an error, so it is neither
 * retried nor reported.
 */
export function useAgentUsageMeter() {
  const { account } = useAuth();

  // The default retry policy already skips 4xx, so the flag-off 404 is not retried.
  const query = useHydraUIAPIQuery({
    queryKey: [AGENT_USAGE_METER_QUERY_KEY, { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.agentUsageMeter.list({
        params: { path: { account_id: String(account.id) } },
        signal,
      }),
  });

  // TanStack keeps the last successful data through an error, so a refetch
  // that 404s (flag turned off) must explicitly stop exposing the stale meter.
  const disabled = query.isError && isMeterDisabled(query.error);
  const meter: AgentUsageMeter | undefined = disabled ? undefined : query.data?.meter;
  return {
    meter,
    isLoading: query.isLoading,
    isError: query.isError && !disabled,
  };
}
